<template>
    <section class="tw:bg-white tw:py-6 tw:px-4">
        <div class="tw:max-w-2xl tw:mx-auto">
            <transition name="fade" mode="out-in">
                <div v-if="step === 'search'" key="search">
                    <CardFront top-bar>
                        <div class="tw:pt-2">
                            <h1 class="tw:text-xl tw:font-bold tw:text-gray-900 tw:text-center tw:mb-6 tw:uppercase">
                                {{ $t('eventos.titulo_buscar_abono', { simpatizante: textoSimpatizante }) }}
                            </h1>

                            <form class="tw:space-y-4" @submit.prevent="handleSearch">
                                <InputText
                                    :label="$t('eventos.numero_abono', { simpatizante: textoSimpatizante })"
                                    name="numero_abonado"
                                    :value="form.numero_abonado"
                                    :rules="'required'"
                                    :disabled="loading"
                                    icon='<path stroke-linecap="round" stroke-linejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.294.876 6.75 6.75 0 0 1-3.294-.876V9l3.294 3.336Z" />'
                                    data-cy="input-numero-abonado"
                                    @input="form.numero_abonado = $event"
                                />

                                <InputText
                                    :label="$t('eventos.dni')"
                                    name="dni"
                                    :value="form.dni"
                                    :rules="'required'"
                                    :disabled="loading"
                                    icon='<path stroke-linecap="round" stroke-linejoin="round" d="M10.5 21h5.25a2.25 2.25 0 0 0 2.25-2.25v-4.5a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 14.25v4.5a2.25 2.25 0 0 0 2.25 2.25h3.75ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />'
                                    data-cy="input-dni"
                                    @input="form.dni = $event"
                                />

                                <InputText
                                    :label="$t('eventos.pin')"
                                    name="pin"
                                    :value="form.pin"
                                    :rules="'required'"
                                    type="password"
                                    :disabled="loading"
                                    icon='<path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />'
                                    data-cy="input-pin"
                                    @input="form.pin = $event"
                                />

                                <div v-if="errorKey" class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:mt-4">
                                    <p class="tw:font-bold tw:text-sm">{{ $t('eventos.error_busqueda', { simpatizante: textoSimpatizante }) }}</p>
                                    <p class="tw:text-sm tw:mt-1">{{ $t(errorKey, { simpatizante: textoSimpatizante }) }}</p>
                                </div>

                                <Button type="submit" :loading="loading" variant="dark" custom-class="tw:mt-6 tw:w-full" data-cy="btn-buscar">
                                    <span v-if="loading">...</span>
                                    <span v-else>{{ $t('header.buscar') }}</span>
                                </Button>
                            </form>
                        </div>
                    </CardFront>
                </div>

                <ConfirmarVinculacion
                    v-else-if="step === 'confirm'"
                    key="confirm"
                    :display-name="displayName"
                    :email-to-bind="emailToBind"
                    :entitlement-type="entitlementType"
                    :error-key="errorKey"
                    :loading="loading"
                    @confirm="handleConfirm"
                    @volver="handleVolver"
                />

                <VinculacionExitosa
                    v-else-if="step === 'success'"
                    key="success"
                    :display-name="displayName"
                    :display-identification="displayIdentification"
                    :display-address="displayAddress"
                    :entitlement-type="entitlementType"
                    :bound-email="boundEmail"
                />
            </transition>
        </div>
    </section>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import { useBuscarAbonado } from '@/composables/front/sga/abonos/useBuscarAbonado';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import ConfirmarVinculacion from './ConfirmarVinculacion.vue';
    import VinculacionExitosa from './VinculacionExitosa.vue';
    import store from '@/store';

    const teatro = computed(() => store.getters.getTeatro || {});
    const textoSimpatizante = computed(() => teatro.value.texto_simpatizante || '');

    const {
        searchResult,
        step,
        loading,
        error,
        emailToBind,
        displayName,
        displayIdentification,
        displayAddress,
        entitlementType,
        searchAbonado,
        bindAbonado,
        goToSuccess,
        reset
    } = useBuscarAbonado();

    // Map error codes to i18n keys (translation done in template)
    const errorKey = computed(() => {
        const map = {
            'NOT_FOUND': 'eventos.error_not_found',
            'BIND_FAILED': 'eventos.error_bind_failed',
            'ALREADY_LINKED': 'eventos.error_already_linked'
        };
        return map[error.value] ?? '';
    });

    const form = ref({
        numero_abonado: '',
        dni: '',
        pin: ''
    });

    const boundEmail = ref('');

    const handleConfirm = async({ email, password }) => {
        const result = await bindAbonado({ email, password });
        if (result) {
            boundEmail.value = email;
            goToSuccess();
        }
    };

    const handleSearch = async() => {
        await searchAbonado({
            subscriber_number: form.value.numero_abonado,
            beneficiary_id_doc: form.value.dni,
            pin: form.value.pin
        });
    };

    const handleVolver = () => {
        reset();
        form.value = { numero_abonado: '', dni: '', pin: '' };
    };
</script>
